import { useEffect, useState } from 'react';
import { supplierPerformanceAPI, type SupplierPerformanceDrilldown } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useView } from '../../../contexts/ViewContext';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { AlertCircle, Loader2 } from 'lucide-react';
import { DeliveryLinesTable, DeliverySuppliersTable, QualityLinesTable, QualitySuppliersTable } from './DrilldownTables';
import { viewParams } from './useSupplierPerformance';

export interface DrilldownRequest {
  /** YYYY-MM */
  period: string;
  kind: 'delivery' | 'quality';
  /** The chart the user clicked, for the title. */
  chartTitle: string;
}

interface PerformanceDrilldownModalProps {
  request: DrilldownRequest | null;
  onClose: () => void;
  /** Current dashboard filters, applied to the drill-down too. */
  division: string;
  supplier: string;
  target: number;
  /** Narrow the dashboard to a supplier picked from the modal. */
  onPickSupplier: (id: string) => void;
}

const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

/**
 * What sits behind one month of a supplier-performance chart: that month per
 * supplier and the individual lines, under the dashboard's current filters.
 */
export default function PerformanceDrilldownModal({
  request, onClose, division, supplier, target, onPickSupplier,
}: PerformanceDrilldownModalProps) {
  const [data, setData] = useState<SupplierPerformanceDrilldown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { getViewQueryParams, viewKey } = useView();

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    supplierPerformanceAPI
      .getDrilldown({
        month: request.period,
        kind: request.kind,
        ...(division ? { division } : {}),
        ...(supplier ? { supplier } : {}),
        ...viewParams(getViewQueryParams()),
      })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Error loading drill-down:', err);
        setError(getApiErrorMessage(err, 'Failed to load the detail for this month'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // viewKey changes whenever getViewQueryParams does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, division, supplier, attempt, viewKey]);

  const pick = (id: number) => {
    onPickSupplier(String(id));
    onClose();
  };

  const summary = () => {
    if (!data) return null;
    if (data.kind === 'quality') {
      return `${data.summary.care_cases.toLocaleString()} customer care cases, ${data.summary.ncrs.toLocaleString()} NCRs, ` +
        `${data.summary.quantity.toLocaleString()} units delivered, from ${data.suppliers.length.toLocaleString()} suppliers.`;
    }
    return `${data.summary.lines.toLocaleString()} delivery lines from ${data.summary.suppliers.toLocaleString()} suppliers — ` +
      `on time ${pct(data.summary.on_time)}, OTIF ${pct(data.summary.otif)}, met required date ${pct(data.summary.required)}.`;
  };

  return (
    <Dialog open={!!request} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[88vh] overflow-y-auto" data-testid="supplier-performance-drilldown">
        <DialogHeader>
          <DialogTitle>{request?.chartTitle} — {data?.label ?? request?.period}</DialogTitle>
          <DialogDescription>
            {summary() ?? 'Loading the detail for this month…'}
            {data?.kind === 'quality' && data.ignores_division ? ' Quality files carry no division, so every division is shown.' : ''}
          </DialogDescription>
        </DialogHeader>

        {loading && !data ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="supplier-performance-drilldown-loading">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center" data-testid="supplier-performance-drilldown-error">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
            <Button variant="outline" size="sm" onClick={() => setAttempt((a) => a + 1)}>Try again</Button>
          </div>
        ) : data && data.lines_total === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground" data-testid="supplier-performance-drilldown-empty">
            Nothing was reported for this month.
          </p>
        ) : data ? (
          <Tabs defaultValue="suppliers">
            <TabsList>
              <TabsTrigger value="suppliers" data-testid="drilldown-tab-suppliers">By supplier ({data.suppliers.length.toLocaleString()})</TabsTrigger>
              <TabsTrigger value="lines" data-testid="drilldown-tab-lines">
                {data.kind === 'quality' ? 'Quality rows' : 'Delivery lines'} ({data.lines_total.toLocaleString()})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="suppliers" className="mt-4">
              <p className="mb-2 text-xs text-muted-foreground">Click a supplier to see only their performance on the dashboard.</p>
              {data.kind === 'quality'
                ? <QualitySuppliersTable rows={data.suppliers} onPick={pick} />
                : <DeliverySuppliersTable rows={data.suppliers} target={target} onPick={pick} />}
            </TabsContent>
            <TabsContent value="lines" className="mt-4">
              {data.lines_truncated && (
                <p className="mb-2 text-xs text-muted-foreground">
                  Showing the first {data.lines.length.toLocaleString()} of {data.lines_total.toLocaleString()}. Filter to a supplier or division to see the rest.
                </p>
              )}
              {data.kind === 'quality' ? <QualityLinesTable rows={data.lines} /> : <DeliveryLinesTable rows={data.lines} />}
            </TabsContent>
          </Tabs>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
